import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { raidAPI, projectsAPI, employeesAPI } from '../services/api';

// ── constants ─────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'risks',        label: '🔴 Risks',        color: '#ef4444' },
  { key: 'assumptions',  label: '🟡 Assumptions',   color: '#f59e0b' },
  { key: 'issues',       label: '🟠 Issues',        color: '#f97316' },
  { key: 'dependencies', label: '🔵 Dependencies',  color: '#3b82f6' },
];

const CATEGORIES    = ['Technical','Resource','Schedule','Budget','Scope','External'];
const ASS_CATS      = ['Technical','Resource','Business','External'];
const PROBABILITIES = ['Low','Medium','High'];
const IMPACTS       = ['Low','Medium','High'];
const RISK_STATUSES = ['Open','Mitigated','Closed'];
const ASS_STATUSES  = ['Open','Validated','Invalidated','Retired'];
const ISS_STATUSES  = ['Open','In Progress','Resolved','Closed'];
const DEP_STATUSES  = ['Pending','Met','Blocked','At Risk'];
const PRIORITIES    = ['Critical','High','Medium','Low'];
const DEP_TYPES     = ['Internal','External'];

const RISK_SCORE_COLOR = (score) => {
  if (score >= 6) return { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' };
  if (score >= 3) return { bg: '#fffbeb', color: '#d97706', border: '#fde68a' };
  return { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' };
};

const IMPACT_BADGE = { High: 'badge-danger', Medium: 'badge-warning', Low: 'badge-info' };
const PRIORITY_BADGE = { Critical: 'badge-danger', High: 'badge-danger', Medium: 'badge-warning', Low: 'badge-info' };
const STATUS_COLORS = {
  Open: 'badge-danger', Mitigated: 'badge-warning', Closed: 'badge-success',
  Validated: 'badge-success', Invalidated: 'badge-danger', Retired: 'badge-info',
  'In Progress': 'badge-warning', Resolved: 'badge-success',
  Pending: 'badge-info', Met: 'badge-success', Blocked: 'badge-danger', 'At Risk': 'badge-warning',
};

const today = new Date().toISOString().split('T')[0];

// ── empty forms ───────────────────────────────────────────────────────────────
const emptyRisk = () => ({
  project_id:'', title:'', description:'', category:'Technical',
  probability:'Medium', impact:'Medium', mitigation_plan:'',
  owner_id:'', status:'Open', date_raised: today, review_date:'',
});
const emptyAssumption = () => ({
  project_id:'', title:'', description:'', category:'Business',
  owner_id:'', impact_if_wrong:'Medium', status:'Open',
  date_raised: today, review_date:'',
});
const emptyIssue = () => ({
  project_id:'', title:'', description:'', category:'Technical',
  priority:'Medium', impact:'Medium', resolution_plan:'',
  owner_id:'', status:'Open', date_raised: today, date_resolved:'',
});
const emptyDependency = () => ({
  project_id:'', title:'', description:'', type:'Internal',
  depends_on:'', owner_id:'', due_date:'', status:'Pending',
  impact_if_missed:'Medium', date_raised: today,
});

// ── risk score preview (mirrors DB generated column) ─────────────────────────
const calcRiskScore = (probability, impact) => {
  const p = { Low:1, Medium:2, High:3 }[probability] || 2;
  const i = { Low:1, Medium:2, High:3 }[impact] || 2;
  return p * i;
};
const calcRiskLevel = (score) => score <= 2 ? 'Low' : score <= 4 ? 'Medium' : 'High';

// ── Excel export ──────────────────────────────────────────────────────────────
const exportToExcel = (risks, assumptions, issues, dependencies) => {
  const wb = XLSX.utils.book_new();

  const riskData = risks.map(r => ({
    'Ref': r.risk_ref, 'Project': r.project_name||'', 'Title': r.title,
    'Category': r.category, 'Probability': r.probability, 'Impact': r.impact,
    'Score': r.risk_score, 'Level': r.risk_level,
    'Mitigation': r.mitigation_plan||'', 'Owner': r.owner_name||'',
    'Status': r.status, 'Date Raised': r.date_raised||'', 'Review Date': r.review_date||'',
  }));
  const assData = assumptions.map(a => ({
    'Ref': a.assumption_ref, 'Project': a.project_name||'', 'Title': a.title,
    'Category': a.category, 'Impact if Wrong': a.impact_if_wrong,
    'Owner': a.owner_name||'', 'Status': a.status,
    'Date Raised': a.date_raised||'', 'Review Date': a.review_date||'',
  }));
  const issData = issues.map(i => ({
    'Ref': i.issue_ref, 'Project': i.project_name||'', 'Title': i.title,
    'Category': i.category, 'Priority': i.priority, 'Impact': i.impact,
    'Resolution': i.resolution_plan||'', 'Owner': i.owner_name||'',
    'Status': i.status, 'Date Raised': i.date_raised||'', 'Date Resolved': i.date_resolved||'',
  }));
  const depData = dependencies.map(d => ({
    'Ref': d.dependency_ref, 'Project': d.project_name||'', 'Title': d.title,
    'Type': d.type, 'Depends On': d.depends_on||'',
    'Impact if Missed': d.impact_if_missed, 'Owner': d.owner_name||'',
    'Due Date': d.due_date||'', 'Status': d.status, 'Date Raised': d.date_raised||'',
  }));

  [
    ['Risks', riskData], ['Assumptions', assData],
    ['Issues', issData], ['Dependencies', depData],
  ].forEach(([name, data]) => {
    if (data.length > 0) {
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = Object.keys(data[0]).map(() => ({ wch: 20 }));
      XLSX.utils.book_append_sheet(wb, ws, name);
    }
  });

  XLSX.writeFile(wb, `RAID_${today}.xlsx`);
};

// ── component ─────────────────────────────────────────────────────────────────
const RAID = () => {
  const [activeTab, setActiveTab]     = useState('risks');
  const [projects, setProjects]       = useState([]);
  const [employees, setEmployees]     = useState([]);

  const [risks, setRisks]             = useState([]);
  const [assumptions, setAssumptions] = useState([]);
  const [issues, setIssues]           = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [loading, setLoading]         = useState(false);

  // Filters per tab
  const [filters, setFilters] = useState({
    risks:        { project_id:'', status:'', category:'', risk_level:'', search:'' },
    assumptions:  { project_id:'', status:'', category:'', search:'' },
    issues:       { project_id:'', status:'', category:'', priority:'', search:'' },
    dependencies: { project_id:'', status:'', type:'', search:'' },
  });

  // Modal
  const [showModal, setShowModal]   = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState('');

  // ── load reference data ────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([projectsAPI.getAll(), employeesAPI.getAll()]).then(([p, e]) => {
      setProjects(p.data); setEmployees(e.data);
    });
  }, []);

  // ── load tab data ──────────────────────────────────────────────────────────
  const loadTab = useCallback(async (tab, f) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f || filters[tab]).forEach(([k, v]) => { if (v) params[k] = v; });
      const loaders = {
        risks:        () => raidAPI.getRisks(params).then(r => setRisks(r.data)),
        assumptions:  () => raidAPI.getAssumptions(params).then(r => setAssumptions(r.data)),
        issues:       () => raidAPI.getIssues(params).then(r => setIssues(r.data)),
        dependencies: () => raidAPI.getDependencies(params).then(r => setDependencies(r.data)),
      };
      await loaders[tab]();
    } catch (err) {
      console.error('Error loading RAID data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadTab(activeTab); }, [activeTab, loadTab]);

  // ── filter helpers ─────────────────────────────────────────────────────────
  const setFilter = (key, val) => {
    const updated = { ...filters, [activeTab]: { ...filters[activeTab], [key]: val } };
    setFilters(updated);
    loadTab(activeTab, updated[activeTab]);
  };
  const clearFilters = () => {
    const cleared = { ...filters, [activeTab]: Object.fromEntries(Object.keys(filters[activeTab]).map(k => [k, ''])) };
    setFilters(cleared);
    loadTab(activeTab, cleared[activeTab]);
  };
  const activeFilterCount = Object.values(filters[activeTab]).filter(Boolean).length;

  // ── modal helpers ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setFormError('');
    setForm({ risks: emptyRisk, assumptions: emptyAssumption, issues: emptyIssue, dependencies: emptyDependency }[activeTab]());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId({ risks: row.risk_id, assumptions: row.assumption_id, issues: row.issue_id, dependencies: row.dependency_id }[activeTab]);
    setFormError('');
    setForm({ ...row });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim())    { setFormError('Title is required.'); return; }
    if (!form.project_id)       { setFormError('Project is required.'); return; }
    if (!form.date_raised)      { setFormError('Date Raised is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, owner_id: form.owner_id || null };
      if (editingId) {
        await { risks: raidAPI.updateRisk, assumptions: raidAPI.updateAssumption,
                issues: raidAPI.updateIssue, dependencies: raidAPI.updateDependency }[activeTab](editingId, payload);
      } else {
        await { risks: raidAPI.createRisk, assumptions: raidAPI.createAssumption,
                issues: raidAPI.createIssue, dependencies: raidAPI.createDependency }[activeTab](payload);
      }
      setShowModal(false);
      loadTab(activeTab);
    } catch (err) {
      setFormError('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, ref) => {
    if (!window.confirm(`Delete ${ref}?`)) return;
    try {
      await { risks: raidAPI.deleteRisk, assumptions: raidAPI.deleteAssumption,
              issues: raidAPI.deleteIssue, dependencies: raidAPI.deleteDependency }[activeTab](id);
      loadTab(activeTab);
    } catch { alert('Error deleting.'); }
  };

  const handleExport = async () => {
    // Load all tabs for export
    const [r, a, i, d] = await Promise.all([
      raidAPI.getRisks({}),
      raidAPI.getAssumptions({}),
      raidAPI.getIssues({}),
      raidAPI.getDependencies({}),
    ]);
    exportToExcel(r.data, a.data, i.data, d.data);
  };

  // ── current tab data ───────────────────────────────────────────────────────
  const tabData = { risks, assumptions, issues, dependencies }[activeTab];
  const tabColor = TABS.find(t => t.key === activeTab)?.color;

  // ── render filters ─────────────────────────────────────────────────────────
  const renderFilters = () => {
    const f = filters[activeTab];
    return (
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2', minWidth: '180px' }}>
            <label className="form-label">Search</label>
            <input className="form-control" placeholder="Title, description…" value={f.search || ''} onChange={e => setFilter('search', e.target.value)} />
          </div>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label className="form-label">Project</label>
            <select className="form-control" value={f.project_id || ''} onChange={e => setFilter('project_id', e.target.value)}>
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
            </select>
          </div>
          <div style={{ flex: '1', minWidth: '130px' }}>
            <label className="form-label">Status</label>
            <select className="form-control" value={f.status || ''} onChange={e => setFilter('status', e.target.value)}>
              <option value="">All</option>
              {({ risks: RISK_STATUSES, assumptions: ASS_STATUSES, issues: ISS_STATUSES, dependencies: DEP_STATUSES }[activeTab]).map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          {(activeTab === 'risks' || activeTab === 'issues') && (
            <div style={{ flex: '1', minWidth: '130px' }}>
              <label className="form-label">Category</label>
              <select className="form-control" value={f.category || ''} onChange={e => setFilter('category', e.target.value)}>
                <option value="">All</option>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          )}
          {activeTab === 'risks' && (
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="form-label">Risk Level</label>
              <select className="form-control" value={f.risk_level || ''} onChange={e => setFilter('risk_level', e.target.value)}>
                <option value="">All</option>
                {['Low','Medium','High'].map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
          )}
          {activeTab === 'issues' && (
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="form-label">Priority</label>
              <select className="form-control" value={f.priority || ''} onChange={e => setFilter('priority', e.target.value)}>
                <option value="">All</option>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          )}
          {activeTab === 'dependencies' && (
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="form-label">Type</label>
              <select className="form-control" value={f.type || ''} onChange={e => setFilter('type', e.target.value)}>
                <option value="">All</option>
                {DEP_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          )}
          {activeFilterCount > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={clearFilters} style={{ marginBottom: '2px' }}>
              ✕ Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── render table ───────────────────────────────────────────────────────────
  const renderTable = () => {
    if (loading) return <div className="loading">Loading…</div>;
    if (tabData.length === 0) return (
      <div className="empty-state">
        <div className="empty-state-icon">{ TABS.find(t=>t.key===activeTab)?.label.split(' ')[0] }</div>
        <div className="empty-state-text">No {activeTab} found</div>
      </div>
    );

    if (activeTab === 'risks') return (
      <div className="table-container">
        <table>
          <thead><tr>
            <th>Ref</th><th>Project</th><th>Title</th><th>Category</th>
            <th>Probability</th><th>Impact</th><th>Score</th><th>Level</th>
            <th>Owner</th><th>Status</th><th>Date Raised</th><th>Review Date</th>
            <th style={{ minWidth: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {risks.map(r => {
              const sc = RISK_SCORE_COLOR(r.risk_score);
              return (
                <tr key={r.risk_id}>
                  <td style={{ fontWeight: '700', color: tabColor }}>{r.risk_ref}</td>
                  <td><Link to={`/projects/${r.project_id}`}>{r.project_name}</Link></td>
                  <td style={{ maxWidth: '200px', fontWeight: '600' }}>{r.title}</td>
                  <td><span className="badge badge-info" style={{ fontSize: '11px' }}>{r.category}</span></td>
                  <td><span className={`badge ${IMPACT_BADGE[r.probability]}`}>{r.probability}</span></td>
                  <td><span className={`badge ${IMPACT_BADGE[r.impact]}`}>{r.impact}</span></td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', fontWeight: '800', fontSize: '14px', background: sc.bg, color: sc.color, border: `2px solid ${sc.border}` }}>
                      {r.risk_score}
                    </span>
                  </td>
                  <td>
                    <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                      {r.risk_level}
                    </span>
                  </td>
                  <td style={{ fontSize: '13px' }}>{r.owner_name || '—'}</td>
                  <td><span className={`badge ${STATUS_COLORS[r.status]}`}>{r.status}</span></td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{r.date_raised}</td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{r.review_date || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm btn-primary" onClick={() => openEdit(r)} style={{ marginRight: '6px' }}>✏️</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r.risk_id, r.risk_ref)}>🗑️</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

    if (activeTab === 'assumptions') return (
      <div className="table-container">
        <table>
          <thead><tr>
            <th>Ref</th><th>Project</th><th>Title</th><th>Category</th>
            <th>Impact if Wrong</th><th>Owner</th><th>Status</th>
            <th>Date Raised</th><th>Review Date</th>
            <th style={{ minWidth: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {assumptions.map(a => (
              <tr key={a.assumption_id}>
                <td style={{ fontWeight: '700', color: tabColor }}>{a.assumption_ref}</td>
                <td><Link to={`/projects/${a.project_id}`}>{a.project_name}</Link></td>
                <td style={{ maxWidth: '220px', fontWeight: '600' }}>{a.title}</td>
                <td><span className="badge badge-info" style={{ fontSize: '11px' }}>{a.category}</span></td>
                <td><span className={`badge ${IMPACT_BADGE[a.impact_if_wrong]}`}>{a.impact_if_wrong}</span></td>
                <td style={{ fontSize: '13px' }}>{a.owner_name || '—'}</td>
                <td><span className={`badge ${STATUS_COLORS[a.status]}`}>{a.status}</span></td>
                <td style={{ fontSize: '12px', color: '#64748b' }}>{a.date_raised}</td>
                <td style={{ fontSize: '12px', color: '#64748b' }}>{a.review_date || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm btn-primary" onClick={() => openEdit(a)} style={{ marginRight: '6px' }}>✏️</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(a.assumption_id, a.assumption_ref)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    if (activeTab === 'issues') return (
      <div className="table-container">
        <table>
          <thead><tr>
            <th>Ref</th><th>Project</th><th>Title</th><th>Category</th>
            <th>Priority</th><th>Impact</th><th>Owner</th><th>Status</th>
            <th>Date Raised</th><th>Date Resolved</th>
            <th style={{ minWidth: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {issues.map(i => (
              <tr key={i.issue_id}>
                <td style={{ fontWeight: '700', color: tabColor }}>{i.issue_ref}</td>
                <td><Link to={`/projects/${i.project_id}`}>{i.project_name}</Link></td>
                <td style={{ maxWidth: '200px', fontWeight: '600' }}>{i.title}</td>
                <td><span className="badge badge-info" style={{ fontSize: '11px' }}>{i.category}</span></td>
                <td><span className={`badge ${PRIORITY_BADGE[i.priority]}`}>{i.priority}</span></td>
                <td><span className={`badge ${IMPACT_BADGE[i.impact]}`}>{i.impact}</span></td>
                <td style={{ fontSize: '13px' }}>{i.owner_name || '—'}</td>
                <td><span className={`badge ${STATUS_COLORS[i.status]}`}>{i.status}</span></td>
                <td style={{ fontSize: '12px', color: '#64748b' }}>{i.date_raised}</td>
                <td style={{ fontSize: '12px', color: '#64748b' }}>{i.date_resolved || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm btn-primary" onClick={() => openEdit(i)} style={{ marginRight: '6px' }}>✏️</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(i.issue_id, i.issue_ref)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    if (activeTab === 'dependencies') return (
      <div className="table-container">
        <table>
          <thead><tr>
            <th>Ref</th><th>Project</th><th>Title</th><th>Type</th>
            <th>Depends On</th><th>Impact if Missed</th><th>Owner</th>
            <th>Due Date</th><th>Status</th><th>Date Raised</th>
            <th style={{ minWidth: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {dependencies.map(d => (
              <tr key={d.dependency_id}>
                <td style={{ fontWeight: '700', color: tabColor }}>{d.dependency_ref}</td>
                <td><Link to={`/projects/${d.project_id}`}>{d.project_name}</Link></td>
                <td style={{ maxWidth: '200px', fontWeight: '600' }}>{d.title}</td>
                <td><span className="badge badge-primary" style={{ fontSize: '11px' }}>{d.type}</span></td>
                <td style={{ fontSize: '13px', color: '#475569' }}>{d.depends_on || '—'}</td>
                <td><span className={`badge ${IMPACT_BADGE[d.impact_if_missed]}`}>{d.impact_if_missed}</span></td>
                <td style={{ fontSize: '13px' }}>{d.owner_name || '—'}</td>
                <td style={{ fontSize: '12px', color: d.due_date && d.due_date < today && d.status !== 'Met' ? '#dc2626' : '#64748b', fontWeight: d.due_date && d.due_date < today && d.status !== 'Met' ? '700' : '400' }}>
                  {d.due_date || '—'}
                </td>
                <td><span className={`badge ${STATUS_COLORS[d.status]}`}>{d.status}</span></td>
                <td style={{ fontSize: '12px', color: '#64748b' }}>{d.date_raised}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm btn-primary" onClick={() => openEdit(d)} style={{ marginRight: '6px' }}>✏️</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(d.dependency_id, d.dependency_ref)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── render form fields per tab ─────────────────────────────────────────────
  const renderFormFields = () => {
    if (activeTab === 'risks') {
      const previewScore = calcRiskScore(form.probability, form.impact);
      const previewLevel = calcRiskLevel(previewScore);
      const sc = RISK_SCORE_COLOR(previewScore);
      return (<>
        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Probability</label>
            <select className="form-control" name="probability" value={form.probability||'Medium'} onChange={handleChange}>
              {PROBABILITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Impact</label>
            <select className="form-control" name="impact" value={form.impact||'Medium'} onChange={handleChange}>
              {IMPACTS.map(i => <option key={i}>{i}</option>)}
            </select>
          </div>
        </div>
        {/* Risk score preview */}
        <div style={{ padding: '12px 16px', borderRadius: '8px', background: sc.bg, border: `1px solid ${sc.border}`, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{ fontSize: '10px', fontWeight: '700', color: sc.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Score</span>
            <span style={{ fontSize: '28px', fontWeight: '900', color: sc.color, lineHeight: 1 }}>{previewScore}</span>
          </div>
          <div>
            <span style={{ fontSize: '14px', fontWeight: '700', color: sc.color }}>Risk Level: {previewLevel}</span>
            <div style={{ fontSize: '11px', color: sc.color, marginTop: '2px', opacity: 0.8 }}>
              {previewLevel === 'High' ? '⚠️ Immediate attention required' : previewLevel === 'Medium' ? '🔔 Monitor closely' : '✅ Low risk — continue monitoring'}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Mitigation Plan</label>
          <textarea className="form-control" name="mitigation_plan" rows="3" value={form.mitigation_plan||''} onChange={handleChange} placeholder="How will this risk be mitigated?" />
        </div>
        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-control" name="status" value={form.status||'Open'} onChange={handleChange}>
              {RISK_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Review Date</label>
            <input type="date" className="form-control" name="review_date" value={form.review_date||''} onChange={handleChange} />
          </div>
        </div>
      </>);
    }

    if (activeTab === 'assumptions') return (<>
      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Impact if Wrong</label>
          <select className="form-control" name="impact_if_wrong" value={form.impact_if_wrong||'Medium'} onChange={handleChange}>
            {IMPACTS.map(i => <option key={i}>{i}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-control" name="status" value={form.status||'Open'} onChange={handleChange}>
            {ASS_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Date Raised *</label>
          <input type="date" className="form-control" name="date_raised" value={form.date_raised||''} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label className="form-label">Review Date</label>
          <input type="date" className="form-control" name="review_date" value={form.review_date||''} onChange={handleChange} />
        </div>
      </div>
    </>);

    if (activeTab === 'issues') return (<>
      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select className="form-control" name="priority" value={form.priority||'Medium'} onChange={handleChange}>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Impact</label>
          <select className="form-control" name="impact" value={form.impact||'Medium'} onChange={handleChange}>
            {IMPACTS.map(i => <option key={i}>{i}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Resolution Plan</label>
        <textarea className="form-control" name="resolution_plan" rows="3" value={form.resolution_plan||''} onChange={handleChange} placeholder="How will this issue be resolved?" />
      </div>
      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-control" name="status" value={form.status||'Open'} onChange={handleChange}>
            {ISS_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Date Resolved</label>
          <input type="date" className="form-control" name="date_resolved" value={form.date_resolved||''} onChange={handleChange} />
        </div>
      </div>
    </>);

    if (activeTab === 'dependencies') return (<>
      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Type</label>
          <select className="form-control" name="type" value={form.type||'Internal'} onChange={handleChange}>
            {DEP_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Impact if Missed</label>
          <select className="form-control" name="impact_if_missed" value={form.impact_if_missed||'Medium'} onChange={handleChange}>
            {IMPACTS.map(i => <option key={i}>{i}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Depends On</label>
        <input className="form-control" name="depends_on" value={form.depends_on||''} onChange={handleChange} placeholder="Which project, team, or system does this depend on?" />
      </div>
      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-control" name="status" value={form.status||'Pending'} onChange={handleChange}>
            {DEP_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Due Date</label>
          <input type="date" className="form-control" name="due_date" value={form.due_date||''} onChange={handleChange} />
        </div>
      </div>
    </>);
  };

  // ── summary counts ─────────────────────────────────────────────────────────
  const summaryData = [
    { tab: 'risks',        label: 'Risks',        count: risks.length,
      sub: `${risks.filter(r=>r.risk_level==='High').length} high` },
    { tab: 'assumptions',  label: 'Assumptions',  count: assumptions.length,
      sub: `${assumptions.filter(a=>a.status==='Open').length} open` },
    { tab: 'issues',       label: 'Issues',       count: issues.length,
      sub: `${issues.filter(i=>i.status==='Open'||i.status==='In Progress').length} active` },
    { tab: 'dependencies', label: 'Dependencies', count: dependencies.length,
      sub: `${dependencies.filter(d=>d.status==='Blocked'||d.status==='At Risk').length} at risk` },
  ];

  // ── page ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">RAID Log</h2>
          <p className="page-subtitle">Risks · Assumptions · Issues · Dependencies</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleExport}>
            📥 Export Excel
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            ＋ Add {TABS.find(t => t.key === activeTab)?.label.split(' ').slice(1).join(' ')}
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        {summaryData.map(s => {
          const tabCfg = TABS.find(t => t.key === s.tab);
          return (
            <div
              key={s.tab}
              className="stat-card"
              onClick={() => setActiveTab(s.tab)}
              style={{ cursor: 'pointer', borderLeft: activeTab === s.tab ? `4px solid ${tabCfg.color}` : '4px solid transparent', transition: 'border 0.2s' }}
            >
              <div className="stat-card-header">
                <div>
                  <div className="stat-card-value" style={{ color: activeTab === s.tab ? tabCfg.color : '#2c3e50' }}>{s.count}</div>
                  <div className="stat-card-label">{s.label}</div>
                </div>
                <div style={{ fontSize: '28px' }}>{tabCfg.label.split(' ')[0]}</div>
              </div>
              <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>{s.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '20px', gap: '0' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '12px 24px', fontSize: '14px',
              fontWeight: activeTab === tab.key ? '700' : '500',
              color: activeTab === tab.key ? tab.color : '#64748b',
              borderBottom: activeTab === tab.key ? `3px solid ${tab.color}` : '3px solid transparent',
              marginBottom: '-2px', whiteSpace: 'nowrap', transition: 'all 0.15s',
            }}
          >
            {tab.label}
            <span style={{
              marginLeft: '8px', background: activeTab === tab.key ? tab.color : '#e2e8f0',
              color: activeTab === tab.key ? '#fff' : '#64748b',
              borderRadius: '99px', padding: '1px 8px', fontSize: '11px', fontWeight: '700',
            }}>
              {{ risks: risks.length, assumptions: assumptions.length, issues: issues.length, dependencies: dependencies.length }[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      {renderFilters()}

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ color: tabColor }}>
            {TABS.find(t => t.key === activeTab)?.label}
            <span style={{ fontSize: '14px', fontWeight: '400', color: '#7f8c8d', marginLeft: '10px' }}>({tabData.length})</span>
          </h3>
        </div>
        {renderTable()}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title" style={{ color: tabColor }}>
                {editingId ? '✏️ Edit' : '＋ New'} {TABS.find(t => t.key === activeTab)?.label.split(' ').slice(1).join(' ')}
              </h3>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Common fields */}
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-control" name="title" value={form.title||''} onChange={handleChange} required placeholder="Brief summary" />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Project *</label>
                  <select className="form-control" name="project_id" value={form.project_id||''} onChange={handleChange} required>
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-control" name="category" value={form.category||''} onChange={handleChange}>
                    {(activeTab === 'assumptions' ? ASS_CATS : CATEGORIES).map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" name="description" rows="3" value={form.description||''} onChange={handleChange} placeholder="Detailed description…" />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Owner</label>
                  <select className="form-control" name="owner_id" value={form.owner_id||''} onChange={handleChange}>
                    <option value="">Select Owner</option>
                    {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.employee_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date Raised *</label>
                  <input type="date" className="form-control" name="date_raised" value={form.date_raised||''} onChange={handleChange} required />
                </div>
              </div>

              {/* Tab-specific fields */}
              {renderFormFields()}

              {formError && (
                <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '14px', marginBottom: '8px' }}>
                  ❌ {formError}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ backgroundColor: tabColor, borderColor: tabColor }}>
                  {saving ? 'Saving…' : editingId ? '💾 Update' : '➕ Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RAID;
